import { daysInMonth, monthKeyFromT } from '../engine/calendar';
import { distributionsInMonth } from '../engine/cashPolicy';
import { driverInputUnit } from '../driverUtils';
import {
    formatMonthLabel,
    formatPercent,
    formatSummaryMoney,
} from '../projectionsFormat';
import type {
    DriverParams,
    DriverType,
    History,
    PayPeriodCadence,
    Recipe,
    RecipeAnchor,
    SchemaEntry,
    View,
} from '../engine/types';
import type { FileIssue } from './types';
import { FILE_ISSUE_CODES, PERCENT_DRIVERS } from './types';
import { trimSchemaLabel } from './utils';

const VIEW_LABELS: Record<View, string> = {
    river_oaks: 'River Oaks',
    highland_park: 'Highland Park',
    ga: 'G&A',
    consolidated: 'Consolidated',
};

export interface AnchorHistoryContext {
    view: View;
    lineKey: string;
    driverType: DriverType;
    params: DriverParams;
    history: History;
    schema: SchemaEntry[];
    lastClosedMonth: string;
}

export interface SeasonalityHistoryContext extends AnchorHistoryContext {
    years: number;
    amplitude?: number;
    source_line?: string;
}

const GPS_KEY = 'is.gross_product_sales';
const DAYS_OPEN_KEY = 'is.days_open_for_business';
const SQFT_KEY = 'is.square_footage';
const BOH_KEY = 'is.boh';
const FOH_KEY = 'is.foh';
const SALARIED_HC_KEY = 'is.salaried_headcount';
const TOTAL_COGS_KEY = 'is.total_cogs';
const NET_REVENUE_KEY = 'is.net_revenue';

function historyValueAtT(
    history: History,
    view: View,
    lineKey: string,
    t: number,
): number | null {
    const idx = history.nHistory - 1 + t;
    if (idx < 0 || idx >= history.nHistory) {
        return null;
    }
    const raw = history.values[view]?.[lineKey]?.[idx];
    if (typeof raw === 'number' && Number.isFinite(raw)) {
        return raw;
    }
    if (typeof raw === 'string' && raw.trim() !== '') {
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return null;
}

function monthKeyAtT(lastClosedMonth: string, t: number): string {
    return monthKeyFromT(lastClosedMonth, t);
}

function daysOpen(history: History, view: View, t: number): number | null {
    const v = historyValueAtT(history, view, DAYS_OPEN_KEY, t);
    return v != null && v > 0 ? v : null;
}

function sqft(history: History, view: View, t: number): number | null {
    const v = historyValueAtT(history, view, SQFT_KEY, t);
    return v != null && v > 0 ? v : null;
}

function fte(history: History, view: View, t: number): number | null {
    const boh = historyValueAtT(history, view, BOH_KEY, t);
    const foh = historyValueAtT(history, view, FOH_KEY, t);
    if (boh == null || foh == null) {
        return null;
    }
    const hourly = boh + foh;
    if (hourly > 0) {
        return hourly;
    }
    if (view === 'ga') {
        const salaried = historyValueAtT(history, view, SALARIED_HC_KEY, t);
        return salaried != null && salaried > 0 ? salaried : null;
    }
    return null;
}

function payDatesInMonth(
    params: DriverParams,
    monthKey: string,
): number {
    const cadence = (params.cadence ?? 'biweekly') as PayPeriodCadence;
    const anchor = params.anchor_date ?? '';
    if (!anchor) {
        return 0;
    }
    return distributionsInMonth(cadence, anchor, monthKey);
}

function ratioLast(
    line: number | null,
    denom: number | null,
): number | null {
    if (line == null || denom == null || denom === 0) {
        return null;
    }
    return line / denom;
}

function trailingRatio(
    history: History,
    view: View,
    lineKey: string,
    denomFn: (t: number) => number | null,
    months: number,
): number | null {
    let sumLine = 0;
    let sumDenom = 0;
    for (let t = 0; t >= -(months - 1); t -= 1) {
        const line = historyValueAtT(history, view, lineKey, t);
        const denom = denomFn(t);
        if (line == null || denom == null) {
            return null;
        }
        sumLine += line;
        sumDenom += denom;
    }
    if (sumDenom === 0) {
        return null;
    }
    return sumLine / sumDenom;
}

function trailingMean(
    history: History,
    view: View,
    lineKey: string,
    months: number,
): number | null {
    let sum = 0;
    for (let t = 0; t >= -(months - 1); t -= 1) {
        const v = historyValueAtT(history, view, lineKey, t);
        if (v == null) {
            return null;
        }
        sum += v;
    }
    return sum / months;
}

function dioDsoDpoLast(
    history: History,
    view: View,
    lineKey: string,
    denomKey: string,
    lastClosedMonth: string,
    t: number,
): number | null {
    const balance = historyValueAtT(history, view, lineKey, t);
    const denom = historyValueAtT(history, 'consolidated', denomKey, t);
    if (balance == null || denom == null || denom === 0) {
        return null;
    }
    const days = daysInMonth(lastClosedMonth, t);
    return (balance * days) / denom;
}

function dioDsoDpoTrailing(
    history: History,
    view: View,
    lineKey: string,
    denomKey: string,
    lastClosedMonth: string,
    months: number,
): number | null {
    const ratios: number[] = [];
    for (let t = 0; t >= -(months - 1); t -= 1) {
        const r = dioDsoDpoLast(history, view, lineKey, denomKey, lastClosedMonth, t);
        if (r == null) {
            return null;
        }
        ratios.push(r);
    }
    return ratios.reduce((s, v) => s + v, 0) / ratios.length;
}

function trailingAvgPerPeriod(
    history: History,
    view: View,
    lineKey: string,
    params: DriverParams,
    lastClosedMonth: string,
    runs: number,
): number | null {
    let sumLine = 0;
    let sumPayDates = 0;
    let found = 0;
    for (let t = 0; t >= -history.nHistory; t -= 1) {
        const monthKey = monthKeyAtT(lastClosedMonth, t);
        const payDates = payDatesInMonth(params, monthKey);
        if (payDates < 1) {
            continue;
        }
        const line = historyValueAtT(history, view, lineKey, t);
        if (line == null) {
            return null;
        }
        sumLine += line;
        sumPayDates += payDates;
        found += 1;
        if (found >= runs) {
            break;
        }
    }
    if (found < runs || sumPayDates === 0) {
        return null;
    }
    return sumLine / sumPayDates;
}

export function anchorFromHistory(
    basis: string,
    monthsOrRuns: number,
    ctx: AnchorHistoryContext,
): number | null {
    const { view, lineKey, driverType, params, history, lastClosedMonth } = ctx;
    const ref = params.ref;

    if (basis === 'last_actual' || basis === 'trailing_avg') {
        const months = monthsOrRuns;
        switch (driverType) {
            case 'dollar_value':
            case 'count_value':
            case 'mom_dollar':
            case 'yoy_dollar':
                return basis === 'last_actual'
                    ? historyValueAtT(history, view, lineKey, 0)
                    : trailingMean(history, view, lineKey, months);
            case 'pct_gross_sales':
                return basis === 'last_actual'
                    ? ratioLast(
                          historyValueAtT(history, view, lineKey, 0),
                          historyValueAtT(history, view, GPS_KEY, 0),
                      )
                    : trailingRatio(history, view, lineKey, (t) => historyValueAtT(history, view, GPS_KEY, t), months);
            case 'pct_of_line':
            case 'dollar_per_line_unit':
                if (!ref) {
                    return null;
                }
                return basis === 'last_actual'
                    ? ratioLast(
                          historyValueAtT(history, view, lineKey, 0),
                          historyValueAtT(history, view, ref, 0),
                      )
                    : trailingRatio(history, view, lineKey, (t) => historyValueAtT(history, view, ref, t), months);
            case 'count_per_open_day':
            case 'dollar_per_open_day':
                return basis === 'last_actual'
                    ? ratioLast(
                          historyValueAtT(history, view, lineKey, 0),
                          daysOpen(history, view, 0),
                      )
                    : trailingRatio(history, view, lineKey, (t) => daysOpen(history, view, t), months);
            case 'count_per_calendar_day':
            case 'dollar_per_calendar_day':
                return basis === 'last_actual'
                    ? ratioLast(
                          historyValueAtT(history, view, lineKey, 0),
                          daysInMonth(lastClosedMonth, 0),
                      )
                    : trailingRatio(
                          history,
                          view,
                          lineKey,
                          (t) => daysInMonth(lastClosedMonth, t),
                          months,
                      );
            case 'dollar_per_sq_ft':
                return basis === 'last_actual'
                    ? ratioLast(historyValueAtT(history, view, lineKey, 0), sqft(history, view, 0))
                    : trailingRatio(history, view, lineKey, (t) => sqft(history, view, t), months);
            case 'dollar_per_1000_sqft_calendar_day': {
                const perDay = (t: number): number | null => {
                    const line = historyValueAtT(history, view, lineKey, t);
                    const sf = sqft(history, view, t);
                    const days = daysInMonth(lastClosedMonth, t);
                    if (line == null || sf == null || days === 0) {
                        return null;
                    }
                    return (line * 1000) / (sf * days);
                };
                if (basis === 'last_actual') {
                    return perDay(0);
                }
                let sum = 0;
                for (let t = 0; t >= -(months - 1); t -= 1) {
                    const v = perDay(t);
                    if (v == null) {
                        return null;
                    }
                    sum += v;
                }
                return sum / months;
            }
            case 'dollar_per_fte':
                return basis === 'last_actual'
                    ? ratioLast(historyValueAtT(history, view, lineKey, 0), fte(history, view, 0))
                    : trailingRatio(history, view, lineKey, (t) => fte(history, view, t), months);
            case 'dio':
                return basis === 'last_actual'
                    ? dioDsoDpoLast(history, view, lineKey, TOTAL_COGS_KEY, lastClosedMonth, 0)
                    : dioDsoDpoTrailing(history, view, lineKey, TOTAL_COGS_KEY, lastClosedMonth, months);
            case 'dso':
                return basis === 'last_actual'
                    ? dioDsoDpoLast(history, view, lineKey, NET_REVENUE_KEY, lastClosedMonth, 0)
                    : dioDsoDpoTrailing(history, view, lineKey, NET_REVENUE_KEY, lastClosedMonth, months);
            case 'dpo':
                return basis === 'last_actual'
                    ? dioDsoDpoLast(history, view, lineKey, TOTAL_COGS_KEY, lastClosedMonth, 0)
                    : dioDsoDpoTrailing(history, view, lineKey, TOTAL_COGS_KEY, lastClosedMonth, months);
            default:
                return null;
        }
    }

    if (basis === 'last_actual_per_period') {
        const monthKey = monthKeyAtT(lastClosedMonth, 0);
        const payDates = payDatesInMonth(params, monthKey);
        const line = historyValueAtT(history, view, lineKey, 0);
        if (line == null || payDates === 0) {
            return null;
        }
        return line / payDates;
    }

    if (basis === 'trailing_avg_per_period') {
        return trailingAvgPerPeriod(history, view, lineKey, params, lastClosedMonth, monthsOrRuns);
    }

    return null;
}

function toPerDaySeries(
    driverType: DriverType,
    value: number,
    history: History,
    view: View,
    lastClosedMonth: string,
    t: number,
): number | null {
    switch (driverType) {
        case 'count_per_open_day':
        case 'dollar_per_open_day': {
            const days = daysOpen(history, view, t);
            return days ? value / days : null;
        }
        case 'count_per_calendar_day':
        case 'dollar_per_calendar_day':
            return value / daysInMonth(lastClosedMonth, t);
        case 'dollar_per_1000_sqft_calendar_day': {
            const sf = sqft(history, view, t);
            const days = daysInMonth(lastClosedMonth, t);
            if (!sf || days === 0) {
                return null;
            }
            return (value * 1000) / (sf * days);
        }
        default:
            return value;
    }
}

export interface ResolveSeasonalityContext {
    view: View;
    lineKey: string;
    driverType: DriverType;
    params: DriverParams;
    history?: History;
    schema?: SchemaEntry[];
    lastClosedMonth: string;
    pathPrefix: string;
}

export function resolveSeasonalityFactors(
    recipe: Recipe,
    ctx: ResolveSeasonalityContext,
): { factors?: Record<string, number>; error?: FileIssue } {
    if (recipe.seasonality?.factors) {
        return { factors: recipe.seasonality.factors };
    }
    if (recipe.seasonality?.from_history) {
        const fh = recipe.seasonality.from_history;
        if (!ctx.history) {
            return {
                error: {
                    code: FILE_ISSUE_CODES.file_recipe_seasonality_history,
                    severity: 'error',
                    path: `${ctx.pathPrefix}.seasonality.from_history`,
                    message: `Seasonality from history on ${ctx.lineKey} requires model history at parse time.`,
                },
            };
        }
        const result = seasonalityFromHistory({
            view: ctx.view,
            lineKey: ctx.lineKey,
            driverType: ctx.driverType,
            params: ctx.params,
            history: ctx.history,
            schema: ctx.schema ?? [],
            lastClosedMonth: ctx.lastClosedMonth,
            years: fh.years,
            amplitude: fh.amplitude,
            source_line: fh.source_line,
        });
        if ('error' in result) {
            return { error: result.error };
        }
        return { factors: result.factors };
    }
    return {};
}

export function seasonalityFromHistory(
    ctx: SeasonalityHistoryContext,
): { factors: Record<string, number> } | { error: FileIssue } {
    const lineKey = ctx.source_line ?? ctx.lineKey;
    const { history, lastClosedMonth, years, amplitude = 1 } = ctx;
    const valuesByMonth: Record<string, number | null> = {};

    for (let t = -(history.nHistory - 1); t <= 0; t += 1) {
        const monthKey = monthKeyAtT(lastClosedMonth, t);
        const raw = historyValueAtT(history, ctx.view, lineKey, t);
        if (raw == null) {
            valuesByMonth[monthKey] = null;
            continue;
        }
        const perDay = toPerDaySeries(ctx.driverType, raw, history, ctx.view, lastClosedMonth, t);
        valuesByMonth[monthKey] = perDay;
    }

    const monthFactors: number[][] = Array.from({ length: 12 }, () => []);
    const yearsFound = new Set<number>();
    for (const mk of Object.keys(valuesByMonth)) {
        yearsFound.add(Number(mk.slice(0, 4)));
    }
    const sortedYears = [...yearsFound].sort((a, b) => b - a);

    let completeYears = 0;
    for (const year of sortedYears) {
        if (completeYears >= years) {
            break;
        }
        const yearVals: number[] = [];
        let valid = true;
        for (let m = 1; m <= 12; m += 1) {
            const mk = `${year}-${String(m).padStart(2, '0')}`;
            const v = valuesByMonth[mk];
            if (v == null || !Number.isFinite(v) || v === 0) {
                valid = false;
                break;
            }
            yearVals.push(v);
        }
        if (!valid) {
            continue;
        }
        completeYears += 1;
        const mean = yearVals.reduce((s, v) => s + v, 0) / 12;
        for (let m = 0; m < 12; m += 1) {
            monthFactors[m]!.push(yearVals[m]! / mean);
        }
    }

    if (completeYears < 1) {
        return {
            error: {
                code: FILE_ISSUE_CODES.file_recipe_seasonality_history,
                severity: 'error',
                path: `$.lines.${ctx.view}["${ctx.lineKey}"].values.seasonality.from_history`,
                message: `Seasonality from history failed for ${lineKey}: fewer than one complete calendar year.`,
            },
        };
    }

    const factors: Record<string, number> = {};
    let sum = 0;
    for (let m = 1; m <= 12; m += 1) {
        const arr = monthFactors[m - 1]!;
        const avg = arr.reduce((s, v) => s + v, 0) / arr.length;
        const damped = 1 + (avg - 1) * amplitude;
        factors[String(m)] = damped;
        sum += damped;
    }
    const norm = sum / 12;
    for (let m = 1; m <= 12; m += 1) {
        factors[String(m)] = factors[String(m)]! / norm;
    }
    return { factors };
}

export interface AnchorDriftContext {
    view: View;
    lineKey: string;
    driverType: DriverType;
    anchor: RecipeAnchor;
    lastClosedMonth: string;
    schema: SchemaEntry[];
    unit?: 'usd' | 'count' | 'pct';
}

function formatRecipeAnchorValue(
    value: number,
    driverType: DriverType,
    unit?: 'usd' | 'count' | 'pct',
): string {
    const resolvedUnit = unit ?? driverInputUnit(driverType as Parameters<typeof driverInputUnit>[0]);
    if (
        resolvedUnit === 'pct' ||
        PERCENT_DRIVERS.has(driverType) ||
        driverType === 'mom_pct' ||
        driverType === 'yoy_pct'
    ) {
        return formatPercent(value);
    }
    if (driverType === 'dio' || driverType === 'dso' || driverType === 'dpo') {
        return `${value} days`;
    }
    if (resolvedUnit === 'count') {
        return String(value);
    }
    return formatSummaryMoney(value);
}

function anchorDriftWindowLabel(anchor: RecipeAnchor, lastClosedMonth: string): string {
    const closed =
        lastClosedMonth.length >= 7 ? lastClosedMonth.slice(0, 7) : lastClosedMonth;
    switch (anchor.basis) {
        case 'last_actual': {
            const abbrev = formatMonthLabel(closed).split('-')[0] ?? closed;
            const yy = String(Number(closed.slice(0, 4)) % 100).padStart(2, '0');
            return `${abbrev} ${yy}`;
        }
        case 'trailing_avg': {
            const months = anchor.months ?? 3;
            const end = closed;
            const start = monthKeyFromT(closed.length === 7 ? `${closed}-01` : closed, -(months - 1));
            const yy = String(Number(end.slice(0, 4)) % 100).padStart(2, '0');
            const startAbbrev = formatMonthLabel(start).split('-')[0] ?? start;
            const endAbbrev = formatMonthLabel(end).split('-')[0] ?? end;
            return `${startAbbrev}–${endAbbrev} ${yy}`;
        }
        case 'last_actual_per_period':
            return `${formatMonthLabel(closed).split('-')[0] ?? closed} ${String(Number(closed.slice(0, 4)) % 100).padStart(2, '0')} per period`;
        case 'trailing_avg_per_period':
            return `${anchor.runs ?? 3}-run avg`;
        default:
            return anchor.basis ?? 'history';
    }
}

export function formatAnchorDriftMessage(
    anchorValue: number,
    historyValue: number,
    ctx: AnchorDriftContext,
): string {
    const entry = ctx.schema.find((row) => row.key === ctx.lineKey);
    const lineLabel = entry ? trimSchemaLabel(entry.label) : ctx.lineKey;
    const store = VIEW_LABELS[ctx.view];
    const authorFmt = formatRecipeAnchorValue(anchorValue, ctx.driverType, ctx.unit);
    const historyFmt = formatRecipeAnchorValue(historyValue, ctx.driverType, ctx.unit);
    const window = anchorDriftWindowLabel(ctx.anchor, ctx.lastClosedMonth);
    return `${lineLabel} (${store}): ${authorFmt} vs ${historyFmt} from ${window}`;
}

export function checkAnchorDrift(
    anchorValue: number,
    historyValue: number | null,
    driftPct: number,
    path: string,
    ctx?: AnchorDriftContext,
): FileIssue | null {
    if (historyValue == null || historyValue === 0) {
        return null;
    }
    const drift = Math.abs(anchorValue - historyValue) / Math.abs(historyValue);
    if (drift > driftPct) {
        const message = ctx
            ? formatAnchorDriftMessage(anchorValue, historyValue, ctx)
            : `anchor.value differs from history-derived ${historyValue} by more than ${Math.round(driftPct * 100)}%.`;
        return {
            code: FILE_ISSUE_CODES.recipe_anchor_drift,
            severity: 'warning',
            path,
            message,
        };
    }
    return null;
}
